import { useEffect, useMemo, useRef, useState } from "react";
import { Crosshair, Gamepad2, Loader2, Skull, Volume2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type DoomChoiceOption = {
  index: number;
  label: string;
  tone: string;
  score: number;
  feedbackTitle: string;
  feedbackBody: string;
};

type DoomUnityStatus = "checking" | "loading" | "ready" | "missing" | "error";

type DoomUnityConfig = {
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  streamingAssetsUrl: string;
  companyName: string;
  productName: string;
  productVersion: string;
};

type DoomUnityInstance = {
  SendMessage?: (gameObjectName: string, methodName: string, value?: string | number) => void;
  Quit?: () => Promise<void>;
};

type CreateDoomUnityInstance = (
  canvas: HTMLCanvasElement,
  config: DoomUnityConfig,
  onProgress?: (progress: number) => void,
) => Promise<DoomUnityInstance>;

type DoomUnityManifest = {
  hasBuild?: boolean;
};

const doomUnityBasePath = "/unity/next-gen-video-lesson-doom";
const doomUnityBuildName = "next-gen-video-lesson-doom";
const doomUnityManifestUrl = `${doomUnityBasePath}/manifest.json`;
const doomUnityLoaderUrl = `${doomUnityBasePath}/Build/${doomUnityBuildName}.loader.js`;

const doomUnityConfig: DoomUnityConfig = {
  dataUrl: `${doomUnityBasePath}/Build/${doomUnityBuildName}.data`,
  frameworkUrl: `${doomUnityBasePath}/Build/${doomUnityBuildName}.framework.js`,
  codeUrl: `${doomUnityBasePath}/Build/${doomUnityBuildName}.wasm`,
  streamingAssetsUrl: `${doomUnityBasePath}/StreamingAssets`,
  companyName: "AIHR",
  productName: "Next Gen Video Lesson DOOM Mode",
  productVersion: "0.1.0-poc",
};

export function DoomUnityArena({
  questionId,
  options,
  onSelectOption,
  onExitDoomMode,
  forceFallback = false,
}: {
  questionId: string;
  options: DoomChoiceOption[];
  onSelectOption: (optionIndex: number) => void;
  onExitDoomMode: () => void;
  forceFallback?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const unityInstanceRef = useRef<DoomUnityInstance | null>(null);
  const [status, setStatus] = useState<DoomUnityStatus>("checking");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const payload = useMemo(
    () =>
      JSON.stringify({
        questionId,
        options,
      }),
    [options, questionId],
  );

  useEffect(() => {
    const handleUnitySelection = (event: Event) => {
      const detail = (event as CustomEvent<{ optionIndex?: number }>).detail;
      if (typeof detail?.optionIndex !== "number") {
        return;
      }

      if (options.some((option) => option.index === detail.optionIndex)) {
        onSelectOption(detail.optionIndex);
      }
    };
    window.addEventListener("cosmos-doom-choice-selected", handleUnitySelection);
    window.addEventListener("cosmos-doom-exit-requested", onExitDoomMode);

    return () => {
      window.removeEventListener("cosmos-doom-choice-selected", handleUnitySelection);
      window.removeEventListener("cosmos-doom-exit-requested", onExitDoomMode);
    };
  }, [onExitDoomMode, onSelectOption, options]);

  useEffect(() => {
    let disposed = false;

    if (forceFallback) {
      setStatus("missing");
      setError(null);
      return () => {
        disposed = true;
      };
    }

    const readManifest = async () => {
      const response = await fetch(doomUnityManifestUrl, {
        cache: "no-store",
      }).catch(() => null);

      if (!response?.ok) {
        return null;
      }

      return response.json().catch(() => null) as Promise<DoomUnityManifest | null>;
    };

    const loadScript = () =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${doomUnityLoaderUrl}"]`,
        );
        if (existing) {
          if (
            (window as Window & {
              createUnityInstance?: CreateDoomUnityInstance;
            }).createUnityInstance
          ) {
            resolve();
            return;
          }

          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener("error", () => reject(new Error("DOOM Unity loader failed.")), {
            once: true,
          });
          return;
        }

        const script = document.createElement("script");
        script.src = doomUnityLoaderUrl;
        script.async = true;
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener("error", () => reject(new Error("DOOM Unity loader failed.")), {
          once: true,
        });
        document.body.appendChild(script);
      });

    const bootUnity = async () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      setStatus("checking");
      setError(null);

      const manifest = await readManifest();
      if (!manifest?.hasBuild) {
        if (!disposed) {
          setStatus("missing");
        }
        return;
      }

      setStatus("loading");
      await loadScript();

      const createUnityInstance = (window as Window & {
        createUnityInstance?: CreateDoomUnityInstance;
      }).createUnityInstance;
      if (!createUnityInstance) {
        throw new Error("DOOM Unity loader did not expose createUnityInstance.");
      }

      const unityInstance = await createUnityInstance(canvas, doomUnityConfig, (value) => {
        if (!disposed) {
          setProgress(Math.round(value * 100));
        }
      });

      unityInstanceRef.current = unityInstance;

      if (disposed) {
        await unityInstance.Quit?.();
        return;
      }

      setProgress(100);
      setStatus("ready");
    };

    void bootUnity().catch((loadError: unknown) => {
      if (disposed) {
        return;
      }

      setError(loadError instanceof Error ? loadError.message : String(loadError));
      setStatus("error");
    });

    return () => {
      disposed = true;
      const unityInstance = unityInstanceRef.current;
      unityInstanceRef.current = null;
      void unityInstance?.Quit?.();
    };
  }, [forceFallback]);

  useEffect(() => {
    if (status !== "ready") {
      return;
    }

    try {
      unityInstanceRef.current?.SendMessage?.("CosmosBridge", "SetOptions", payload);
    } catch (sendError) {
      const message = sendError instanceof Error ? sendError.message : String(sendError);
      setError(`Unity options bridge failed: ${message}`);
    }
  }, [payload, status]);

  const statusCopy = useMemo(() => {
    if (status === "ready") {
      return "Unity DOOM arena active";
    }
    if (status === "loading") {
      return `Loading Unity DOOM ${progress}%`;
    }
    if (forceFallback) {
      return "React DOOM targets active";
    }
    if (status === "missing") {
      return "Unity DOOM build not deployed";
    }
    if (status === "error") {
      return "Unity DOOM failed to load";
    }
    return "Checking Unity DOOM build";
  }, [forceFallback, progress, status]);

  const showFallback = forceFallback || status === "missing" || status === "error" || status === "checking";

  return (
    <div className="flex min-h-[min(70dvh,560px)] flex-col overflow-hidden bg-[#120607] sm:min-h-[560px]">
      <div className="flex flex-col gap-3 border-b border-red-300/15 bg-black/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex min-w-0 items-start gap-3 sm:items-center">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-red-300/25 bg-red-500/15 text-red-100 sm:h-10 sm:w-10">
            <Skull className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-black uppercase tracking-[0.18em] text-red-100">
              DOOM Mode
            </div>
            <div className="text-[11px] leading-5 text-white/48 sm:text-xs">
              <span className="sm:hidden">WASD · shoot · aim · reload after 2 shots</span>
              <span className="hidden sm:inline">
                WASD move · left click shoot · right click aim · two shots before reload
              </span>
            </div>
          </div>
        </div>
        <span className="inline-flex max-w-full shrink-0 items-center gap-2 self-start rounded-full border border-red-200/15 bg-red-500/10 px-3 py-1 text-[11px] font-bold text-red-100 sm:self-auto sm:text-xs">
          {status === "loading" || status === "checking" ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          ) : (
            <Gamepad2 className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="truncate">{statusCopy}</span>
        </span>
      </div>

      <div className="relative min-h-0 flex-1">
        <canvas
          ref={canvasRef}
          id="unity-next-gen-video-lesson-doom"
          className={cn(
            "aspect-video min-h-[min(52dvh,500px)] w-full bg-black sm:min-h-[500px] sm:aspect-auto sm:h-[500px]",
            status === "ready" || status === "loading" ? "block" : "hidden",
          )}
        />

        {status === "ready" && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-16 w-16 -translate-x-1/2 -translate-y-1/2">
            <div className="absolute left-1/2 top-0 h-5 w-0.5 -translate-x-1/2 rounded-full bg-red-100/90 shadow-[0_0_12px_rgba(248,113,113,0.9)]" />
            <div className="absolute bottom-0 left-1/2 h-5 w-0.5 -translate-x-1/2 rounded-full bg-red-100/90 shadow-[0_0_12px_rgba(248,113,113,0.9)]" />
            <div className="absolute left-0 top-1/2 h-0.5 w-5 -translate-y-1/2 rounded-full bg-red-100/90 shadow-[0_0_12px_rgba(248,113,113,0.9)]" />
            <div className="absolute right-0 top-1/2 h-0.5 w-5 -translate-y-1/2 rounded-full bg-red-100/90 shadow-[0_0_12px_rgba(248,113,113,0.9)]" />
            <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-red-100/90 bg-red-500/25" />
          </div>
        )}

        {status === "loading" && (
          <div className="absolute inset-x-4 bottom-4 rounded-2xl border border-red-200/15 bg-black/75 p-4 backdrop-blur sm:inset-x-8 sm:bottom-8">
            <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-[0.22em] text-white/55">
              Loading arena
              <span>{progress}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-red-400 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {showFallback && (
          <DoomFallbackArena
            error={error}
            options={options}
            onSelectOption={onSelectOption}
          />
        )}
      </div>
    </div>
  );
}

function DoomFallbackArena({
  error,
  options,
  onSelectOption,
}: {
  error: string | null;
  options: DoomChoiceOption[];
  onSelectOption: (optionIndex: number) => void;
}) {
  return (
    <div className="absolute inset-0 overflow-hidden overflow-y-auto bg-[radial-gradient(circle_at_50%_20%,rgba(185,28,28,0.28),transparent_34%),linear-gradient(180deg,#1a0808_0%,#070304_72%)] p-4 sm:p-6">
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-red-900/45 to-transparent" />
      <div className="absolute bottom-0 left-1/2 h-48 w-full max-w-[720px] -translate-x-1/2 rounded-t-full bg-red-950/35 blur-3xl" />
      <div className="relative z-10 grid min-h-full content-between gap-5 sm:gap-6">
        <div className="max-w-3xl">
          <p className="mb-2 text-[11px] font-black uppercase tracking-[0.24em] text-red-200 sm:mb-3 sm:text-xs sm:tracking-[0.3em]">
            Unity arena slot
          </p>
          <h2 className="text-2xl font-black leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
            Shoot the demon holding your answer.
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/62 sm:mt-3">
            Unity is unavailable right now, so these fallback targets keep the episode stable.
            Click a target to register the same option a demon hit would send.
          </p>
          {error && (
            <p className="mt-3 rounded-lg border border-red-300/20 bg-red-500/10 p-3 text-xs text-red-100">
              {error}
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
          {options.map((option) => (
            <button
              key={option.index}
              onClick={() => onSelectOption(option.index)}
              className="group relative overflow-hidden rounded-2xl border border-red-300/20 bg-black/45 p-4 text-left shadow-2xl shadow-black/30 transition hover:-translate-y-1 hover:border-red-200/70 hover:bg-red-500/20 sm:rounded-3xl sm:p-5"
            >
              <div className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-red-500/20 text-red-100 transition group-hover:bg-red-300 group-hover:text-[#120607] sm:right-4 sm:top-4 sm:h-10 sm:w-10">
                <Crosshair className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div className="mb-4 grid h-12 w-12 place-items-center rounded-2xl border border-red-300/25 bg-red-500/15 text-red-100 sm:mb-5 sm:h-16 sm:w-16">
                <Skull className="h-7 w-7 sm:h-9 sm:w-9" />
              </div>
              <div className="mb-2 text-[11px] font-black uppercase tracking-[0.22em] text-red-200">
                Demon target {option.index + 1}
              </div>
              <div className="pr-10 text-sm font-black text-white sm:pr-12 sm:text-base">{option.label}</div>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white/65">
                <Volume2 className="h-3.5 w-3.5" />
                {option.tone} · {option.score > 0 ? `+${option.score}` : "+0"}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
